import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import {
  ArrowUpDown,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Flag,
  Link2,
  Play,
  Search,
  X,
} from "lucide-react";

import { auth, db } from "../Security/firebase-config";
import { useAuth } from "../context/AuthContext";
import { feedbackReportsCollection } from "../utils/communityFirestorePaths";
import "./PublicClips.css";
import PublicClipRemoveButton from "../components/core-components/PublicClipRemoveButton";

const CLIP_SORT_OPTIONS = [
  { value: "newest-first", label: "Newest first" },
  { value: "oldest-first", label: "Oldest first" },
  { value: "alphabetical-asc", label: "A to Z" },
  { value: "alphabetical-desc", label: "Z to A" },
  { value: "largest-first", label: "Largest first" },
  { value: "smallest-first", label: "Smallest first" },
];

const PUBLIC_CLIP_REPORT_REASONS = [
  { value: "inappropriate-content", label: "Inappropriate content" },
  { value: "harassment-or-hate", label: "Harassment or hate" },
  { value: "spam-or-misleading", label: "Spam or misleading" },
  { value: "privacy-or-safety", label: "Privacy or safety concern" },
  { value: "copyright-or-ownership", label: "Copyright or ownership issue" },
  { value: "other", label: "Other" },
];

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeDateValue(value) {
  if (!value) return null;
  if (typeof value === "string") {
    return Number.isFinite(Date.parse(value)) ? new Date(value).toISOString() : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value).toISOString();
  }
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate().toISOString();
    } catch {
      return null;
    }
  }
  if (typeof value?.seconds === "number") {
    return new Date(value.seconds * 1000).toISOString();
  }
  return null;
}

function formatDateLabel(value) {
  const normalized = normalizeDateValue(value);
  if (!normalized) return "Recently published";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(normalized));
}

function formatDurationLabel(durationMs) {
  const totalSeconds = Math.max(0, Math.round(Number(durationMs || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function shouldFallbackToUnorderedPublicClips(error) {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes("query requires an index") ||
    message.includes("index is currently building")
  );
}

async function copyTextToClipboard(value) {
  const nextValue = normalizeText(value);
  if (!nextValue) {
    throw new Error("Nothing is available to copy yet.");
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(nextValue);
    return;
  }

  const element = document.createElement("textarea");
  element.value = nextValue;
  element.setAttribute("readonly", "true");
  element.style.position = "fixed";
  element.style.opacity = "0";
  document.body.appendChild(element);
  element.select();
  document.execCommand("copy");
  document.body.removeChild(element);
}

function coercePublicClip(raw, docId) {
  const clipId = normalizeText(raw?.clipId) || docId;
  const title = normalizeText(raw?.title);
  const publicVideoUrl = normalizeText(raw?.publicVideoUrl);
  const status = normalizeText(raw?.status) || "published";
  const visibility = normalizeText(raw?.visibility) || "public";

  if (!clipId || !title || !publicVideoUrl || status !== "published" || visibility !== "public") {
    return null;
  }

  const publishedAt = normalizeDateValue(raw?.publishedAt);
  const publishedAtMs = publishedAt ? Date.parse(publishedAt) : Number.NaN;

  return {
    clipId,
    title,
    description: normalizeText(raw?.description),
    ownerUid: normalizeText(raw?.ownerUid),
    ownerUsername: normalizeText(raw?.ownerUsername) || "Unknown",
    publicVideoUrl,
    publicPosterUrl: normalizeText(raw?.publicPosterUrl),
    durationMs: Math.max(0, Number(raw?.durationMs || 0)),
    shareUrl: normalizeText(raw?.shareUrl),
    publishedAt,
    publishedAtMs: Number.isFinite(publishedAtMs) ? publishedAtMs : 0,
  };
}

function getPublicClipShareUrl(clip) {
  if (normalizeText(clip?.shareUrl)) {
    return clip.shareUrl;
  }

  if (typeof window !== "undefined") {
    return new URL(`/clips/${clip.clipId}`, window.location.origin).toString();
  }

  return `/clips/${clip.clipId}`;
}

function isInteractiveFormTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName;
  return (
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    tagName === "SELECT" ||
    target.isContentEditable
  );
}

export default function PublicClipsPage() {
  const { userData } = useAuth();
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [sortMode, setSortMode] = useState("newest-first");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [selectedClipId, setSelectedClipId] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportNotes, setReportNotes] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const sortMenuRef = useRef(null);
  const feedbackTimeoutRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function loadPublicClips() {
      setLoading(true);
      setFeedback(null);

      try {
        const newestFirstQuery = query(
          collection(db, "publicClips"),
          where("status", "==", "published"),
          where("visibility", "==", "public"),
          orderBy("publishedAt", "desc"),
          limit(120),
        );
        const fallbackQuery = query(
          collection(db, "publicClips"),
          where("status", "==", "published"),
          where("visibility", "==", "public"),
        );

        let snapshot;
        try {
          snapshot = await getDocs(newestFirstQuery);
        } catch (error) {
          if (!shouldFallbackToUnorderedPublicClips(error)) {
            throw error;
          }
          snapshot = await getDocs(fallbackQuery);
        }

        if (cancelled) return;

        const nextClips = snapshot.docs
          .map((docSnapshot) => coercePublicClip(docSnapshot.data() || {}, docSnapshot.id))
          .filter(Boolean)
          .sort((left, right) => right.publishedAtMs - left.publishedAtMs)
          .slice(0, 120);

        setClips(nextClips);
      } catch (error) {
        if (cancelled) return;
        setFeedback({
          tone: "error",
          message: error instanceof Error ? error.message : "Public clips could not be loaded.",
        });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadPublicClips();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!feedback) return undefined;

    if (feedbackTimeoutRef.current != null) {
      window.clearTimeout(feedbackTimeoutRef.current);
    }

    feedbackTimeoutRef.current = window.setTimeout(() => {
      setFeedback(null);
      feedbackTimeoutRef.current = null;
    }, 5000);

    return () => {
      if (feedbackTimeoutRef.current != null) {
        window.clearTimeout(feedbackTimeoutRef.current);
        feedbackTimeoutRef.current = null;
      }
    };
  }, [feedback]);

  useEffect(() => {
    if (!sortMenuOpen) return undefined;

    function handlePointerDown(event) {
      if (!sortMenuRef.current?.contains(event.target)) {
        setSortMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [sortMenuOpen]);

  const visibleClips = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase();
    const filteredClips = clips.filter((clip) => {
      if (!normalizedQuery) return true;
      const haystack = [clip.title, clip.description, clip.ownerUsername]
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });

    const sortedClips = [...filteredClips];
    sortedClips.sort((left, right) => {
      switch (sortMode) {
        case "oldest-first":
          return left.publishedAtMs - right.publishedAtMs;
        case "alphabetical-asc":
          return left.title.localeCompare(right.title, undefined, { sensitivity: "base" });
        case "alphabetical-desc":
          return right.title.localeCompare(left.title, undefined, { sensitivity: "base" });
        case "largest-first":
        case "smallest-first":
        case "newest-first":
        default:
          return right.publishedAtMs - left.publishedAtMs;
      }
    });

    return sortedClips;
  }, [clips, searchValue, sortMode]);

  const selectedClipIndex = useMemo(
    () => visibleClips.findIndex((clip) => clip.clipId === selectedClipId),
    [selectedClipId, visibleClips],
  );

  const selectedClip = useMemo(() => {
    if (selectedClipIndex >= 0) {
      return visibleClips[selectedClipIndex] || null;
    }
    return clips.find((clip) => clip.clipId === selectedClipId) || null;
  }, [clips, selectedClipId, selectedClipIndex, visibleClips]);

  useEffect(() => {
    if (!selectedClip) return undefined;

    function handleViewerKeydown(event) {
      if (event.key === "Escape") {
        if (reportOpen && !reportSubmitting) {
          setReportOpen(false);
          return;
        }

        setSelectedClipId(null);
        return;
      }

      if (reportOpen || isInteractiveFormTarget(event.target)) {
        return;
      }

      if (event.key === "ArrowLeft" && selectedClipIndex > 0) {
        setSelectedClipId(visibleClips[selectedClipIndex - 1]?.clipId || null);
        return;
      }

      if (event.key === "ArrowRight" && selectedClipIndex >= 0) {
        setSelectedClipId(
          visibleClips[selectedClipIndex + 1]?.clipId || selectedClip.clipId,
        );
      }
    }

    window.addEventListener("keydown", handleViewerKeydown);
    return () => {
      window.removeEventListener("keydown", handleViewerKeydown);
    };
  }, [reportOpen, reportSubmitting, selectedClip, selectedClipIndex, visibleClips]);

  function handleOpenClip(clip) {
    setReportOpen(false);
    setSelectedClipId(clip.clipId);
  }

  function handleCloseClipViewer() {
    setReportOpen(false);
    setSelectedClipId(null);
  }

  function handleClipStep(direction) {
    if (selectedClipIndex < 0) return;
    const nextClip = visibleClips[selectedClipIndex + direction];
    if (!nextClip) return;
    setReportOpen(false);
    setSelectedClipId(nextClip.clipId);
  }

  async function handleShareClip(clip) {
    try {
      await copyTextToClipboard(getPublicClipShareUrl(clip));
      setFeedback({
        tone: "success",
        message: "Public clip link copied.",
      });
    } catch (error) {
      setFeedback({
        tone: "error",
        message:
          error instanceof Error ? error.message : "The public clip link could not be copied.",
      });
    }
  }

  function handleOpenClipPage(clip) {
    window.open(getPublicClipShareUrl(clip), "_blank", "noopener,noreferrer");
  }

  function handleOpenReportDialog() {
    if (!selectedClip) return;
    setReportReason(PUBLIC_CLIP_REPORT_REASONS[0]?.value || "");
    setReportNotes("");
    setReportOpen(true);
  }

  function handleCloseReportDialog() {
    if (reportSubmitting) return;
    setReportOpen(false);
  }

  async function handleSubmitReport() {
    if (!selectedClip) return;

    if (!auth.currentUser?.uid) {
      setFeedback({
        tone: "error",
        message: "Sign in before reporting public clips.",
      });
      return;
    }

    if (!reportReason) {
      setFeedback({
        tone: "error",
        message: "Select a report reason before sending the clip report.",
      });
      return;
    }

    try {
      setReportSubmitting(true);
      const selectedReasonLabel =
        PUBLIC_CLIP_REPORT_REASONS.find((reason) => reason.value === reportReason)?.label ||
        "Other";
      const reporterUsername =
        normalizeText(userData?.username) ||
        normalizeText(auth.currentUser.displayName) ||
        normalizeText(auth.currentUser.email) ||
        "Unknown";

      await addDoc(feedbackReportsCollection(db), {
        status: "submitted",
        createdAt: serverTimestamp(),
        reporterId: auth.currentUser.uid,
        reporterUsername,
        postId: selectedClip.clipId,
        postType: "publicClip",
        postOwnerId: selectedClip.ownerUid || null,
        postTeamId: null,
        postDisplayName: selectedClip.title,
        reason: `Public clip report: ${selectedReasonLabel}`,
        reportReason: reportReason,
        otherText: reportNotes.trim().slice(0, 200),
        shareUrl: getPublicClipShareUrl(selectedClip),
      });

      setReportOpen(false);
      setFeedback({
        tone: "success",
        message: "Clip report submitted.",
      });
    } catch (error) {
      setFeedback({
        tone: "error",
        message:
          error instanceof Error ? error.message : "The clip report could not be submitted.",
      });
    } finally {
      setReportSubmitting(false);
    }
  }

  return (
    <div className="public-clips-page">
      <section className="public-clips-browser">
        <header className="public-clips-browser__header">
          <div className="public-clips-browser__header-copy">
            <div>
              <p className="public-clips-browser__eyebrow">Clips</p>
              <h1>Discover Clips</h1>
              <p>
                Browse community gameplay clips, copy share links, and report public uploads
                directly from the web app.
              </p>
            </div>
          </div>

          <div className="public-clips-browser__view-tabs" role="tablist" aria-label="Clip library view">
            <span
              role="tab"
              aria-selected="true"
              className="public-clips-browser__view-tab is-active"
            >
              Discover Clips
            </span>
          </div>
        </header>

        <div className="public-clips-browser__controls-card">
          <div className="public-clips-browser__toolbar">
            <label className="public-clips-browser__search" aria-label="Search clips">
              <Search size={14} aria-hidden="true" />
              <input
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search Discover Clips"
              />
            </label>

            <div className="public-clips-browser__sort-anchor" ref={sortMenuRef}>
              <button
                type="button"
                className="public-clips-browser__icon-button public-clips-browser__sort-button"
                onClick={() => setSortMenuOpen((current) => !current)}
                aria-label="Sort clips"
                title="Sort clips"
                aria-expanded={sortMenuOpen}
              >
                <ArrowUpDown size={15} aria-hidden="true" />
              </button>

              {sortMenuOpen ? (
                <div className="public-clips-browser__sort-menu" role="menu" aria-label="Clip sort options">
                  {CLIP_SORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`public-clips-browser__sort-option ${
                        sortMode === option.value ? "is-active" : ""
                      }`}
                      onClick={() => {
                        setSortMode(option.value);
                        setSortMenuOpen(false);
                      }}
                      role="menuitemradio"
                      aria-checked={sortMode === option.value}
                    >
                      <span>{option.label}</span>
                      {sortMode === option.value ? <Check size={14} aria-hidden="true" /> : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="public-clips-browser__content">
          <div className="public-clips-browser__grid">
            {loading ? (
              <div className="public-clips-browser__empty-state">Loading Discover Clips...</div>
            ) : null}

            {!loading &&
              visibleClips.map((clip) => (
                <article
                  key={clip.clipId}
                  className="public-clips-browser__card"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenClip(clip)}
                  aria-label={`Open public clip ${clip.title}`}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleOpenClip(clip);
                    }
                  }}
                >
                  <div className="public-clips-browser__preview" title={clip.description || clip.title}>
                    {clip.publicPosterUrl ? (
                      <img src={clip.publicPosterUrl} alt="" aria-hidden="true" />
                    ) : (
                      <div className="public-clips-browser__preview-empty">No preview frame</div>
                    )}

                    <div className="public-clips-browser__play-button" aria-hidden="true">
                      <Play size={18} fill="currentColor" strokeWidth={1.8} />
                    </div>

                    <div className="public-clips-browser__tile-actions">
                      <button
                        type="button"
                        className="public-clips-browser__tile-action"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenClipPage(clip);
                        }}
                        aria-label="Open public clip page"
                        title="Open public clip page"
                      >
                        <ExternalLink size={12} aria-hidden="true" />
                      </button>
                    </div>

                    <div className="public-clips-browser__tile-caption">
                      <strong>{clip.title}</strong>
                      <span>{clip.ownerUsername}</span>
                      <span>
                        {formatDateLabel(clip.publishedAt)} | {formatDurationLabel(clip.durationMs)}
                      </span>
                    </div>
                  </div>
                </article>
              ))}

            {!loading && !visibleClips.length ? (
              <div className="public-clips-browser__empty-state">
                No clips matched in Discover Clips yet.
              </div>
            ) : null}
          </div>
        </div>

        {selectedClip ? (
          <div
            className="public-clips-browser__viewer-backdrop"
            onClick={handleCloseClipViewer}
          >
            <div
              className="public-clips-browser__viewer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="public-discover-clip-title"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="public-clips-browser__icon-button public-clips-browser__viewer-nav public-clips-browser__viewer-nav--left"
                onClick={() => handleClipStep(-1)}
                disabled={selectedClipIndex <= 0}
                aria-label="Previous discover clip"
                title="Previous discover clip"
              >
                <ChevronLeft size={22} aria-hidden="true" />
              </button>

              <div className="public-clips-browser__viewer-shell">
                <button
                  type="button"
                  className="public-clips-browser__icon-button public-clips-browser__viewer-close app-cancel-control"
                  onClick={handleCloseClipViewer}
                  aria-label="Close discover clip viewer"
                  title="Close discover clip viewer"
                >
                  <X size={16} aria-hidden="true" />
                </button>

                <div className="public-clips-browser__viewer-video-shell">
                  <video
                    className="public-clips-browser__viewer-video"
                    controls
                    playsInline
                    poster={selectedClip.publicPosterUrl || undefined}
                    src={selectedClip.publicVideoUrl}
                  />
                </div>

                <div className="public-clips-browser__viewer-info">
                  <div className="public-clips-browser__viewer-copy">
                    <h3 id="public-discover-clip-title">{selectedClip.title}</h3>
                    <div className="public-clips-browser__viewer-meta">
                      <span>{selectedClip.ownerUsername}</span>
                      <span>{formatDateLabel(selectedClip.publishedAt)}</span>
                      <span>{formatDurationLabel(selectedClip.durationMs)}</span>
                    </div>
                    <p>
                      {selectedClip.description || "No description was added for this public clip."}
                    </p>
                  </div>

                  <div className="public-clips-browser__viewer-actions">
                    <PublicClipRemoveButton clip={selectedClip} onFeedback={setFeedback}
                      onRemoved={(clipId) => {
                        setClips((current) => current.filter((clip) => clip.clipId !== clipId));
                        setSelectedClipId(null);
                      }} />
                    <button
                      type="button"
                      className="public-clips-browser__icon-button"
                      onClick={() => void handleShareClip(selectedClip)}
                      aria-label="Share link"
                      title="Share link"
                    >
                      <Link2 size={16} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="public-clips-browser__icon-button"
                      onClick={handleOpenReportDialog}
                      aria-label="Report clip"
                      title="Report clip"
                    >
                      <Flag size={16} aria-hidden="true" />
                    </button>
                  </div>
                </div>

                {reportOpen ? (
                  <div
                    className="public-clips-browser__report-backdrop"
                    onClick={handleCloseReportDialog}
                  >
                    <div
                      className="public-clips-browser__report-dialog"
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="public-discover-report-title"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="public-clips-browser__report-header">
                        <div>
                          <p className="public-clips-browser__eyebrow">Report Clip</p>
                          <h4 id="public-discover-report-title">Report {selectedClip.title}</h4>
                          <p>Select the reason that best matches the issue, then confirm the report.</p>
                        </div>
                        <button
                          type="button"
                          className="public-clips-browser__icon-button app-cancel-control"
                          onClick={handleCloseReportDialog}
                          disabled={reportSubmitting}
                          aria-label="Close report menu"
                          title="Close report menu"
                        >
                          <X size={14} aria-hidden="true" />
                        </button>
                      </div>

                      <div className="public-clips-browser__report-options" role="radiogroup" aria-label="Report reason">
                        {PUBLIC_CLIP_REPORT_REASONS.map((reason) => (
                          <button
                            key={reason.value}
                            type="button"
                            className={`public-clips-browser__report-option ${
                              reportReason === reason.value ? "is-active" : ""
                            }`}
                            onClick={() => setReportReason(reason.value)}
                            role="radio"
                            aria-checked={reportReason === reason.value}
                          >
                            <span>{reason.label}</span>
                            {reportReason === reason.value ? <Check size={14} aria-hidden="true" /> : null}
                          </button>
                        ))}
                      </div>

                      <label className="public-clips-browser__report-notes">
                        <span>Extra notes (optional)</span>
                        <textarea
                          value={reportNotes}
                          onChange={(event) => setReportNotes(event.target.value.slice(0, 200))}
                          placeholder="Add any extra detail for the moderation team."
                          disabled={reportSubmitting}
                        />
                      </label>

                      <div className="public-clips-browser__report-actions">
                        <button
                          type="button"
                          className="public-clips-browser__button public-clips-browser__button--secondary app-cancel-control"
                          onClick={handleCloseReportDialog}
                          disabled={reportSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="public-clips-browser__button public-clips-browser__button--primary"
                          onClick={() => void handleSubmitReport()}
                          disabled={reportSubmitting || !reportReason}
                        >
                          {reportSubmitting ? "Sending report..." : "Confirm report"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <button
                type="button"
                className="public-clips-browser__icon-button public-clips-browser__viewer-nav public-clips-browser__viewer-nav--right"
                onClick={() => handleClipStep(1)}
                disabled={selectedClipIndex < 0 || selectedClipIndex >= visibleClips.length - 1}
                aria-label="Next discover clip"
                title="Next discover clip"
              >
                <ChevronRight size={22} aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : null}
      </section>

      {feedback ? (
        <div
          className={`public-clips-browser__feedback ${
            feedback.tone === "error" ? "is-error" : "is-success"
          }`}
        >
          <span>{feedback.message}</span>
          <button
            type="button"
            className="public-clips-browser__feedback-close app-cancel-control"
            onClick={() => setFeedback(null)}
            aria-label="Dismiss notification"
            title="Dismiss notification"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
